/**
 * StoryViewer - Scene-by-scene reader & editor for story-type posts
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import type { Post, MediaItem } from '../types';
import { STORAGE_API } from '../config';

interface StoryScene {
  id: string;
  title: string;
  narrative: string;
  mood?: string;
  location?: string;
  illustrationPrompt?: string;
  illustrationId?: number;
  audioId?: number;
  videoId?: number;
}

interface StoryContent {
  scenes: StoryScene[];
}

interface StoryMeta {
  type: 'story';
  language: string;
  story_group: string;
  narrator: string;
  color: string;
  cover: string;
  region: string;
  duration: string;
  subtitle: string;
}

type ViewMode = 'read' | 'edit';

interface StoryViewerProps {
  post: Post;
  viewMode: ViewMode;
  onSave: (content: string) => void;
  isSaving: boolean;
}

function parseStoryContent(content: string | null): StoryContent | null {
  if (!content) return null;
  try {
    const parsed = JSON.parse(content);
    if (parsed.scenes && Array.isArray(parsed.scenes)) {
      return parsed as StoryContent;
    }
  } catch {
    // Not valid JSON
  }
  return null;
}

function getStoryMeta(post: Post): StoryMeta | null {
  const meta = post.metadata_json as Record<string, unknown> | null;
  if (!meta || meta.type !== 'story') return null;
  return meta as unknown as StoryMeta;
}

const STORAGE_BASE = `${STORAGE_API}/storage/media`;

function getSceneIllustration(scene: StoryScene, media: MediaItem[]): MediaItem | null {
  // New: direct illustrationId in scene JSON
  if (scene.illustrationId) {
    return { id: 0, storage_id: scene.illustrationId, position: 0, caption: scene.id, role: 'illustration', media_url: `${STORAGE_BASE}/${scene.illustrationId}`, created_at: '' };
  }
  // Legacy: lookup via PostMedia table
  return media.find(m => m.role === 'illustration' && m.caption === scene.id) ?? null;
}

function getSceneVideo(scene: StoryScene, media: MediaItem[]): MediaItem | null {
  if (scene.videoId) {
    return { id: 0, storage_id: scene.videoId, position: 0, caption: scene.id, role: 'video', media_url: `${STORAGE_BASE}/${scene.videoId}`, created_at: '' };
  }
  return media.find(m => m.role === 'video' && m.caption === scene.id) ?? null;
}

export function StoryViewer({ post, viewMode, onSave, isSaving }: StoryViewerProps) {
  const [currentScene, setCurrentScene] = useState(0);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const meta = useMemo(() => getStoryMeta(post), [post]);
  const storyContent = useMemo(() => parseStoryContent(post.content), [post.content]);

  // Editable scenes state — initialized from post content
  const [editScenes, setEditScenes] = useState<StoryScene[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const narrativeRef = useRef<HTMLTextAreaElement>(null);

  // Sync edit state when post content changes (e.g. after save)
  useEffect(() => {
    const parsed = parseStoryContent(post.content);
    if (parsed) {
      setEditScenes(parsed.scenes.map(s => ({ ...s })));
      setHasChanges(false);
    }
  }, [post.content]);

  const scenes = viewMode === 'edit' ? editScenes : (storyContent?.scenes ?? []);
  const scene = scenes[currentScene];
  const color = meta?.color ?? '#10b981';

  const goToScene = useCallback((index: number) => {
    if (index >= 0 && index < scenes.length) {
      setCurrentScene(index);
    }
  }, [scenes.length]);

  const nextScene = useCallback(() => goToScene(currentScene + 1), [currentScene, goToScene]);
  const prevScene = useCallback(() => goToScene(currentScene - 1), [currentScene, goToScene]);

  // Update a field of the current scene
  const updateSceneField = useCallback((field: keyof StoryScene, value: string) => {
    setEditScenes(prev => {
      const updated = [...prev];
      updated[currentScene] = { ...updated[currentScene], [field]: value };
      return updated;
    });
    setHasChanges(true);
  }, [currentScene]);

  // Build updated JSON and call onSave
  const handleSave = useCallback(() => {
    const updatedContent: StoryContent = { scenes: editScenes };
    onSave(JSON.stringify(updatedContent));
    setHasChanges(false);
  }, [editScenes, onSave]);

  // Keyboard navigation (only in preview mode, not when editing text)
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      // Don't intercept keys when editing text
      if (viewMode === 'edit') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        nextScene();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        prevScene();
      } else if (e.key === 'Escape' && lightboxSrc) {
        setLightboxSrc(null);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [nextScene, prevScene, lightboxSrc, viewMode]);

  // Auto-resize textarea
  useEffect(() => {
    if (viewMode === 'edit' && narrativeRef.current) {
      const ta = narrativeRef.current;
      ta.style.height = 'auto';
      ta.style.height = ta.scrollHeight + 'px';
    }
  }, [viewMode, currentScene, scene?.narrative]);

  if (!storyContent || scenes.length === 0) {
    return (
      <div className="story-viewer-empty">
        <p>Keine Szenen gefunden.</p>
      </div>
    );
  }

  const illustration = scene ? getSceneIllustration(scene, post.media) : null;
  const video = scene ? getSceneVideo(scene, post.media) : null;

  return (
    <div className="story-viewer">
      {/* Story Header */}
      <div className="story-viewer-header" style={{ borderBottomColor: color }}>
        <div className="story-viewer-header-left">
          {meta?.cover && (
            <span className="story-viewer-cover">{meta.cover}</span>
          )}
          <div className="story-viewer-info">
            <h1 className="story-viewer-title" style={{ color }}>{post.title}</h1>
            {meta?.subtitle && (
              <p className="story-viewer-subtitle">{meta.subtitle}</p>
            )}
            <div className="story-viewer-meta-row">
              {meta?.narrator && (
                <span className="story-viewer-meta-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                  {meta.narrator}
                </span>
              )}
              {meta?.region && (
                <span className="story-viewer-meta-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  {meta.region}
                </span>
              )}
              {meta?.duration && (
                <span className="story-viewer-meta-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  {meta.duration}
                </span>
              )}
              {meta?.language && (
                <span className="story-viewer-meta-item story-viewer-lang" style={{ borderColor: color, color }}>
                  {meta.language.toUpperCase()}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Save indicator in header area */}
        {viewMode === 'edit' && hasChanges && (
          <button
            className="btn btn-primary btn-sm story-viewer-save"
            onClick={handleSave}
            disabled={isSaving}
            style={{ backgroundColor: color, borderColor: color }}
          >
            {isSaving ? 'Speichern...' : 'Speichern'}
          </button>
        )}
      </div>

      {/* Scene Navigation Bar */}
      <div className="story-scene-nav">
        <div className="story-scene-dots">
          {scenes.map((s, i) => (
            <button
              key={s.id}
              className={`story-scene-dot ${i === currentScene ? 'active' : ''}`}
              style={i === currentScene ? { backgroundColor: color } : undefined}
              onClick={() => goToScene(i)}
              title={s.title}
            />
          ))}
        </div>
        <span className="story-scene-counter">
          Szene {currentScene + 1} / {scenes.length}
        </span>
      </div>

      {/* Scene Content */}
      {scene && (
        <div className="story-scene" key={scene.id}>
          {/* Scene Illustration */}
          {illustration && (
            <div className="story-scene-illustration">
              <img
                src={`${illustration.media_url}?variant=medium`}
                alt={scene.title}
                onClick={() => setLightboxSrc(illustration.media_url)}
                onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }}
              />
            </div>
          )}

          {/* Scene Body — Preview Mode */}
          {viewMode === 'read' && (
            <div className="story-scene-body">
              <h2 className="story-scene-title" style={{ color }}>
                {scene.title}
              </h2>

              <p className="story-scene-narrative">
                {scene.narrative}
              </p>

              {(scene.mood || scene.location) && (
                <div className="story-scene-meta">
                  {scene.mood && (
                    <span className="story-scene-tag" style={{ borderColor: color, color }}>
                      {scene.mood}
                    </span>
                  )}
                  {scene.location && (
                    <span className="story-scene-tag">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      {scene.location}
                    </span>
                  )}
                </div>
              )}

              {video && (
                <div className="story-scene-video">
                  <video
                    src={video.media_url}
                    controls
                    preload="metadata"
                    poster={`${video.media_url}?variant=thumbnail&format=jpg`}
                  />
                </div>
              )}
            </div>
          )}

          {/* Scene Body — Edit Mode */}
          {viewMode === 'edit' && (
            <div className="story-scene-body story-scene-edit">
              <div className="story-edit-field">
                <label className="story-edit-label">Szenen-Titel</label>
                <input
                  type="text"
                  className="story-edit-input"
                  value={scene.title}
                  onChange={(e) => updateSceneField('title', e.target.value)}
                  style={{ borderColor: color }}
                />
              </div>

              <div className="story-edit-field">
                <label className="story-edit-label">Erzähltext</label>
                <textarea
                  ref={narrativeRef}
                  className="story-edit-textarea"
                  value={scene.narrative}
                  onChange={(e) => updateSceneField('narrative', e.target.value)}
                  style={{ borderColor: color }}
                />
              </div>

              <div className="story-edit-row">
                <div className="story-edit-field">
                  <label className="story-edit-label">Stimmung</label>
                  <input
                    type="text"
                    className="story-edit-input"
                    value={scene.mood ?? ''}
                    onChange={(e) => updateSceneField('mood', e.target.value)}
                    placeholder="z.B. mystisch, dramatisch..."
                  />
                </div>

                <div className="story-edit-field">
                  <label className="story-edit-label">Ort</label>
                  <input
                    type="text"
                    className="story-edit-input"
                    value={scene.location ?? ''}
                    onChange={(e) => updateSceneField('location', e.target.value)}
                    placeholder="z.B. Besucherzentrum"
                  />
                </div>
              </div>

              {video && (
                <div className="story-scene-video">
                  <video
                    src={video.media_url}
                    controls
                    preload="metadata"
                    poster={`${video.media_url}?variant=thumbnail&format=jpg`}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Scene Navigation Buttons */}
      <div className="story-scene-buttons">
        <button
          className="btn btn-secondary"
          onClick={prevScene}
          disabled={currentScene === 0}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Vorherige Szene
        </button>
        <button
          className="btn btn-primary"
          onClick={nextScene}
          disabled={currentScene === scenes.length - 1}
          style={currentScene < scenes.length - 1 ? { backgroundColor: color, borderColor: color } : undefined}
        >
          Nächste Szene
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* All Scenes Overview (collapsed) */}
      <details className="story-scenes-overview">
        <summary style={{ color }}>Alle {scenes.length} Szenen anzeigen</summary>
        <ol className="story-scenes-list">
          {scenes.map((s, i) => (
            <li
              key={s.id}
              className={i === currentScene ? 'active' : ''}
              onClick={() => goToScene(i)}
            >
              <strong>{s.title}</strong>
              {s.mood && <span className="story-scenes-list-mood">{s.mood}</span>}
            </li>
          ))}
        </ol>
      </details>

      {/* Unsaved changes indicator */}
      {viewMode === 'edit' && hasChanges && (
        <div className="story-unsaved-hint">
          Ungespeicherte Änderungen
        </div>
      )}

      {/* Lightbox */}
      {lightboxSrc && (
        <div className="lightbox-overlay" onClick={() => setLightboxSrc(null)}>
          <button className="lightbox-close" onClick={() => setLightboxSrc(null)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          <img
            src={lightboxSrc}
            alt=""
            className="lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
